// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import CartLineContainer, { CartLineContainerStyles } from "@eisinc/App/Components/Cart/CartLineContainer";
import { CertificationBadges } from "@eisinc/App/Components/CertificationBadges/CertificationBadges";
import ProductImagesBadges, {
    ProductImagesBadgesStyles,
} from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import Notify from "@eisinc/App/Components/Notify";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { isProductInActive, parseBoolean } from "@eisinc/App/Utilities/helpers";
import { DatePickerPresentationProps } from "@eisinc/MobiusOverrides/DatePicker";
import Select, { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import ProductAddToCartButton, {
    ProductAddToCartLinkStyles,
} from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@eisinc/Overrides/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@eisinc/Overrides/Widgets/Cart/CartLineQuantity";
import CartWarning from "@eisinc/XDL/Icons/CartWarning";
import Edit from "@eisinc/XDL/Icons/Edit";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { formatDate } from "@eisinc/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showRemoveAction?: boolean;
    showAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;

    isInStock?: boolean;
    isPartial?: boolean;
    isBackOrder?: boolean;
    editable?: boolean;
    showPONumber?: boolean;
    showDate?: boolean;
    isCustomGrouping?: boolean;
    showBadging?: boolean;
    qtyContent?: React.ReactNode;
    availabilityContent?: React.ReactNode;
    totalContent?: React.ReactNode;
    customerProductNumber?: string;
    productNumber?: string;
    showQtyOrderedShipped?: boolean;
    showAddToCart?: boolean;
    showShipmentData?: boolean;
    handleShowShipmentDataClick?: Function;
    productId?: string | null;
    qtyOne?: string;
    qtyTwo?: string;
    pricingOnDate?: boolean;
    showCutWire?: boolean;
    productIdAsId?: boolean;
    total?: string;
    dontShowCertification?: boolean;
    showQuotedPrice?: boolean;
    useModalForCertification?: boolean;
    editableQty?: boolean;
    fallbackDateToDash?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    cartSettings: getSettingsCollection(state).cartSettings,
    productInfo: getProductInfoFromList(state, "orderDetails", props.productId as string),
    requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
    requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
    isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state)?.type === "CheckoutReviewAndSubmitPage",
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    lineNumberGridItem?: InjectableCss;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityGridItem?: GridItemProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    middleContentAreaGridItem?: GridItemProps;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cutWireLink?: LinkPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    // XNG-Change
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    wishListAndCheckboxes?: InjectableCss;
    certificationsStyles?: InjectableCss;
    certificationLabels?: CheckboxPresentationProps;
    addToCartButtonWrapper?: InjectableCss;
    addToCartButtonStyles?: ProductAddToCartLinkStyles;
    shipmentDataWrapper?: ClickablePresentationProps;
    shipmentDataIcon?: IconPresentationProps;
    shipmentDataText?: TypographyPresentationProps;
    ctaButtons?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    priceHeading?: TypographyPresentationProps;
    longProductDescription?: ProductDescriptionStyles;

    reqDeliveryDate?: InjectableCss;
    qtyWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    totalPriceWrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
    totalWrapper?: InjectableCss;
    totalHeading?: TypographyPresentationProps;
    deliveryDateStyles?: SmallHeadingAndTextStyles;
    productPrice?: ProductPriceStyles;
    removeLink?: LinkPresentationProps;
    errorMessage?: TypographyPresentationProps;
    cartLineContainer?: CartLineContainerStyles;
    productAvailabilityWrapper?: InjectableCss;
    certBadgesWrapper?: InjectableCss;
    mfrBadge?: InjectableCss;
    eisBadge?: InjectableCss;
    poLineWrapper?: InjectableCss;
    toggleLink?: LinkPresentationProps;
    POLabel?: TypographyProps;
    editablePoTextField?: TextFieldPresentationProps;
    readOnlyPOLineText?: SmallHeadingAndTextStyles;
    qtyOrderedAndQtyShippedWrapper?: InjectableCss;
    dateTooltipWrapper?: InjectableCss;
    customImagesBadgesStyles?: ProductImagesBadgesStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    showAddToList,
    isInStock,
    isPartial,
    isBackOrder,
    editable,
    showPONumber,
    showDate,
    cartSettings,
    isCustomGrouping,
    showBadging,
    qtyContent,
    availabilityContent,
    totalContent,
    customerProductNumber,
    productNumber,
    showQtyOrderedShipped,
    showAddToCart,
    showShipmentData,
    handleShowShipmentDataClick,
    productInfo,
    qtyOne,
    qtyTwo,
    pricingOnDate,
    showCutWire,
    productIdAsId,
    requestedDeliveryDate,
    requestedPickupDate,
    isPickUp,
    total,
    isCheckoutReviewAndSubmitPage,
    showQuotedPrice,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    isTabletScreen,
    dontShowCertification,
    useModalForCertification,
    editableQty,
    fallbackDateToDash,
}) => {
    const [showCutWireButton, setShowCutWireButton] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [eisCertificate, setEisCertificate] = React.useState(cartLine.properties["eis/GCGCertification"] === "true");
    const [mfrCertificate, setMfrCertificate] = React.useState(cartLine.properties["mfrCertification"] === "true");
    const [isChangePONumerModalOpen, setChangePONumerModalOpen] = React.useState(false);
    const [poLineNumber, setPoLineNumber] = React.useState(cartLine.properties["poLineNumber"]);
    const [isPoLineNumberUpdating, setPoLineNumberUpdating] = React.useState(false);
    const dispatch = useAppDispatch();
    const [removeClicked, setRemoveClicked] = React.useState(false);

    React.useEffect(() => {
        if (cartLine.properties["allowCutWire"] === "true" && showCutWire) {
            setShowCutWireButton(true);
        }
    }, []);

    const oldDate = cartLine.properties["requestedByDate"]
        ? new Date(cartLine.properties["requestedByDate"])
        : isPickUp
        ? requestedPickupDate
        : requestedDeliveryDate;

    const priceValueStyle = css`
        display: block;
        font-size: ${fontConfig.singleLineSmall.size}px;
        line-height: ${fontConfig.singleLineSmall.lineHeight};
    `;

    React.useEffect(() => {
        if (
            cartLine.properties?.hasOwnProperty("poLineNumber") &&
            poLineNumber !== cartLine.properties["poLineNumber"]
        ) {
            setPoLineNumber(cartLine.properties["poLineNumber"]);
        }
    }, [cartLine.properties["poLineNumber"]]);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                    properties: { ...cartLine?.properties, oldQty: cartLine?.qtyOrdered?.toString() || "1" },
                },
                cartId: cart.id,
            } as any);
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    React.useEffect(() => {
        if (eisCertificate.toString() !== cartLine.properties["eis/GCGCertification"]) {
            setEisCertificate(cartLine.properties["eis/GCGCertification"] === "true");
        }
        if (mfrCertificate.toString() !== cartLine.properties["mfrCertification"]) {
            setMfrCertificate(cartLine.properties["mfrCertification"] === "true");
        }
    }, [cartLine]);

    const eisCertificationHandler = (event: React.SyntheticEvent, value: boolean) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    "eis/GCGCertification": value.toString(),
                },
            },
            cartId: cart.id,
        } as any);
    };

    const mfrCertificationHandler = (event: React.SyntheticEvent, value: boolean) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    mfrCertification: value.toString(),
                },
            },
            cartId: cart.id,
        } as any);
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                cartId: cart.id,
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            } as any);
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }
        setRemoveClicked(true);

        removeCartLine({ cartLineId: cartLine.id, cartId: cart.id } as any);
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });

            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const handleSetPoNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (!raw) {
            setPoLineNumber("");
            return;
        }

        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed)) {
            setPoLineNumber("");
            return;
        }

        setPoLineNumber(parsed.toLocaleString());
    };

    const onPoNumberBlurHandler = () => {
        if (poLineNumber && poLineNumber !== cartLine.properties["poLineNumber"]) {
            setPoLineNumberUpdating(true);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        poLineNumber: poLineNumber?.toString(),
                    },
                },
                cartId: cart.id,
                onSuccess: () => {
                    setChangePONumerModalOpen(false);
                    setPoLineNumberUpdating(false);
                    toasterContext.addToast({
                        body: translate("Line Number Updated"),
                        messageType: "success",
                    });
                },
            } as any);
            return;
        }
        setPoLineNumber(cartLine.properties["poLineNumber"]);
        setChangePONumerModalOpen(false);
    };

    const handleChangePoLineNumber = () => {
        if (poLineNumber !== cartLine.properties["poLineNumber"]) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        poLineNumber: poLineNumber?.toString(),
                    },
                },
                cartId: cart.id,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Number Updated"),
                        messageType: "success",
                    });
                },
            } as any);
        } else {
            setChangePONumerModalOpen(false);
        }
    };

    const openCutWireModal = () => {
        dispatch({
            type: "CutWire/SetModalOpened",
            isOpen: true,
            cartLine,
        });
    };

    const errorMessages: React.ReactNode[] = [];
    const badges = cartLine?.properties["badges"] ? JSON.parse(cartLine?.properties["badges"]) : null;

    if (isProductInActive(cartLine)) {
        errorMessages.push(translate("Inactive product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const lineNumber = (cartLine as any).linePOReference || (cartLine as any).lineNumber;
    const product = { ...cartLine };
    const unitPriceDisplay = (cartLine as any)?.unitPriceDisplay;
    const unitOfMeasure = (cartLine as any)?.unitOfMeasure;
    const realTimePricingUomString = (cartLine as any)?.properties?.realTimePricingUom;
    const pricePerString = (cartLine as any)?.properties?.pricePer;
    const realTimePricingUom = realTimePricingUomString && parseInt(realTimePricingUomString, 10);
    const pricePer = pricePerString && parseInt(pricePerString, 10);

    const heading = isChangePONumerModalOpen
        ? "Save PO Line #"
        : poLineNumber?.length === 0
        ? "Add PO Line #"
        : "Edit PO Line #";

    const iconSrc = isChangePONumerModalOpen ? Check : Edit;

    if (productIdAsId) {
        product["id"] = product["productId"] as string;
    }

    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);

    const customerProductNumberValue = customerProductNumber
        ? (cartLine as any)[customerProductNumber]
        : cartLine.customerName;

    return (
        <CartLineContainer
            extendedStyles={styles.cartLineContainer}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            includeReqDeliveryDate={showDate}
            includeQtyOrderedShipped={showQtyOrderedShipped}
        >
            <CartLineContainer.Image>
                {/* xng-change - using figure as the product image wrapper */}
                <StyledWrapper as="figure">
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                    <img
                        src={
                            "product" in cartLine
                                ? (cartLine as any).product.mediumImagePath || (cartLine as any).product.smallImagePath
                                : cartLine.smallImagePath || (cartLine as any).mediumImagePath
                        }
                        alt={"product" in cartLine ? (cartLine as any).product.imageAltText : cartLine.altText}
                        className="product-image-print"
                    />
                </StyledWrapper>
                {cartLine.isRestricted && (
                    <Typography {...styles.errorMessage}>
                        <Icon size={12} color="warning" src={CartWarning} />
                        {translate("Error")}
                    </Typography>
                )}
                <CertificationBadges
                    hasEisCertification={parseBoolean(cartLine.properties["eis/GCGCertification"])}
                    hasMfrCertification={parseBoolean(cartLine.properties.mfrCertification)}
                />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <StyledWrapper {...styles.productInfoGridItem}>
                    <ProductDescription
                        product={cartLine}
                        extendedStyles={product.brand ? styles.productDescription : styles.longProductDescription}
                    />
                    <StyledWrapper {...styles.productPartNumbersGridItem}>
                        <ProductPartNumbers
                            showLabel={true}
                            showSpecNumber={true}
                            showCustomerName={true}
                            showManufacturerItem={true}
                            showProductNumber={!cartLine.manufacturerItem || !customerProductNumberValue}
                            productNumber={productNumber ? (cartLine as any)[productNumber] : cartLine.erpNumber}
                            customerProductNumber={customerProductNumberValue}
                            manufacturerItem={cartLine.manufacturerItem}
                            specNumber={cartLine.properties["specNumber"] || ""}
                            extendedStyles={styles.productPartNumbers}
                        />
                        {showVmiBinDetails && currentMode === "Vmi" && (
                            <StyledWrapper {...styles.binInformationsGridItem}>
                                <ProductBinNumbers
                                    locationId={cart.vmiLocationId}
                                    binId={cartLine.vmiBinId}
                                    isCondensed={false}
                                    extendedStyles={styles.productBinNumbers}
                                />
                            </StyledWrapper>
                        )}
                        {cartLine.brand && (
                            <ProductBrand
                                showLabel={true}
                                brand={cartLine.brand}
                                extendedStyles={styles.productBrand}
                            />
                        )}
                        {promotions && promotions.length > 0 && (
                            <StyledWrapper>
                                <Typography as="strong">{translate("Promotion:")}</Typography>
                                {promotions.map(promotion => (
                                    <Typography {...styles.promotionNameText} key={promotion.id} as="span">
                                        {promotion.name},
                                    </Typography>
                                ))}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>

                    {cart?.showCostCode && !cartLine.isPromotionItem && (
                        <StyledWrapper {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    aria-label={translate("Cost Code")}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </StyledWrapper>
                    )}

                    {showBadging && (
                        <StyledWrapper {...styles.badgesWrapper}>
                            {badges && (
                                <ProductImagesBadges
                                    product={cartLine}
                                    badges={badges}
                                    extendedStyles={styles.customImagesBadgesStyles}
                                />
                            )}
                        </StyledWrapper>
                    )}

                    {availabilityContent && (
                        <StyledWrapper {...styles.productAvailabilityWrapper}>{availabilityContent}</StyledWrapper>
                    )}
                </StyledWrapper>
            </CartLineContainer.Description>

            {showDate && (
                <CartLineContainer.ReqDeliveryDate>
                    <StyledWrapper {...styles.dateTooltipWrapper}>
                        <SmallHeadingAndText
                            text={
                                fallbackDateToDash
                                    ? "--"
                                    : cartLine.properties["requestedByDate"]
                                    ? formatDate(
                                          convertDateOnlyStringToDate(cartLine.properties["requestedByDate"]) ?? null,
                                      )
                                    : (translate("Ships ASAP") as any)
                            }
                            heading={translate("Req. Delivery Date")}
                            extendedStyles={styles.deliveryDateStyles}
                        />
                        {parseBoolean(cartLine.properties["wasScheduledDateInPast"]) && (
                            <Tooltip text={siteMessage("Cart_Past_Scheduled_Date_Tooltip")} />
                        )}
                    </StyledWrapper>
                </CartLineContainer.ReqDeliveryDate>
            )}
            <CartLineContainer.PriceUnit>
                {!cart?.cartNotPriced && cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                    <StyledWrapper
                        className={`${cart && pricingOnDate === undefined ? "cartPricing" : ""} ${
                            editable && !showDate ? "" : "not-editable"
                        }`}
                    >
                        <Typography {...styles.priceHeading}>{translate("Price/Unit")} </Typography>
                        {cart && pricingOnDate !== undefined && pricingOnDate && (
                            <>
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    showInvalidPriceMessage={true}
                                    showPricePer={true}
                                    showUnitPriceDisplay={true}
                                    extendedStyles={styles.productPrice}
                                    {...{ showQuotedPrice }}
                                />
                            </>
                        )}
                        {cart && pricingOnDate !== undefined && !pricingOnDate && "----"}
                        {cart && pricingOnDate === undefined && (
                            <ProductPrice
                                showLabel={false}
                                product={cartLine}
                                currencySymbol={cart.currencySymbol}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                showInvalidPriceMessage={true}
                                showUOM={true}
                                extendedStyles={styles.productPrice}
                            />
                        )}
                    </StyledWrapper>
                )}
                {!cart?.cartNotPriced && !cartLine.pricing && (
                    <>
                        <StyledWrapper>
                            <StyledWrapper {...styles.priceHeading}> {translate("Price/Unit")} </StyledWrapper>

                            <StyledWrapper css={priceValueStyle}>
                                {unitPriceDisplay}
                                {(unitOfMeasure || (!!pricePer && pricePer > 1)) && ` / `}
                                {!!pricePer && pricePer > 1 && (
                                    <>
                                        {pricePer}
                                        &nbsp;
                                    </>
                                )}
                                {unitOfMeasure && (
                                    <>
                                        {!!realTimePricingUom && realTimePricingUom > 1 && realTimePricingUom}
                                        {unitOfMeasure}
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper className="total-column">
                            <StyledWrapper {...styles.priceHeading}> {translate("Ext. Price")} </StyledWrapper>
                            <StyledWrapper css={priceValueStyle}>
                                {total ? (cartLine as any)[total] : (cartLine as any).extendedUnitNetPriceDisplay}
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                )}
                {cart?.cartNotPriced && (
                    <Typography
                        size="14px"
                        lineHeight="16px"
                        css={css`
                            flex: 1 0 auto;
                            text-align: center;
                        `}
                    >
                        {siteMessage("RealTimePricing_PriceLoadFailed")}
                    </Typography>
                )}
            </CartLineContainer.PriceUnit>
            <CartLineContainer.Qty>
                <StyledWrapper {...styles.qtyWrapper} as="section">
                    {!qtyContent && !showQtyOrderedShipped && (
                        <>
                            <CartLineQuantity
                                showLabel={isTabletScreen}
                                cart={cart}
                                editable={editableQty && !errorMessages.length}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                extendedStyles={styles.quantity}
                                qtyDataName={qtyOne}
                                label={showQtyOrderedShipped ? translate("Qty Ordered") : translate("Qty")}
                            />
                        </>
                    )}
                    {qtyContent && qtyContent}
                    {!qtyContent && showQtyOrderedShipped && (
                        <StyledWrapper {...styles.qtyOrderedAndQtyShippedWrapper}>
                            <CartLineQuantity
                                showLabel={isTabletScreen}
                                cart={cart}
                                editable={editableQty && !errorMessages.length}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                extendedStyles={styles.quantity}
                                qtyDataName={qtyOne}
                                label={showQtyOrderedShipped ? translate("Qty Ordered") : translate("Qty")}
                            />
                            <CartLineQuantity
                                showLabel={showQtyOrderedShipped}
                                cart={cart}
                                editable={editableQty && !showDate && !errorMessages.length}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                extendedStyles={styles.quantity}
                                qtyDataName={qtyTwo}
                                label={showQtyOrderedShipped ? translate("Qty Shipped") : translate("Qty")}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                <StyledWrapper as="section" {...styles.totalPriceWrapper}>
                    {!cart?.cartNotPriced && cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <StyledWrapper {...styles.totalWrapper}>
                            {((pricingOnDate !== undefined && !pricingOnDate) || cartLine.quoteRequired) && "--"}
                            {!cartLine.quoteRequired &&
                                cartLine.pricing &&
                                cartLine.pricing?.unitNetPrice !== 0 &&
                                !cart?.cartNotPriced &&
                                !totalContent &&
                                (pricingOnDate === undefined || pricingOnDate) && (
                                    <>
                                        <Typography {...styles.totalHeading}>{translate("Total")}</Typography>
                                        <Typography
                                            {...styles.extendedUnitNetPriceText}
                                            data-test-selector="cartline_extendedUnitNetPrice"
                                        >
                                            {enableVat && vatPriceDisplay !== "DisplayWithoutVat" && (
                                                <>{cartLine.pricing.extendedUnitRegularPriceWithVatDisplay}</>
                                            )}

                                            {!(enableVat && vatPriceDisplay !== "DisplayWithoutVat") &&
                                            cartLine.properties["qtyOrdered"]
                                                ? `${cart.currencySymbol}${(
                                                      parseInt(cartLine.properties["qtyOrdered"], 10) *
                                                      cartLine.pricing!.unitNetPrice
                                                  ).toFixed(2)}`
                                                : cartLine.pricing!.extendedUnitNetPriceDisplay}
                                        </Typography>
                                    </>
                                )}
                            {totalContent && totalContent}

                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                    {!cart?.cartNotPriced && !cartLine.pricing && (
                        <>
                            <StyledWrapper className="total-column">
                                <StyledWrapper {...styles.priceHeading}> {translate("Ext. Price")} </StyledWrapper>
                                <StyledWrapper css={priceValueStyle}>
                                    {total ? (cartLine as any)[total] : (cartLine as any).extendedUnitNetPriceDisplay}
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                    {cart?.cartNotPriced && (
                        <Typography
                            size="14px"
                            lineHeight="16px"
                            css={css`
                                flex: 1 0 auto;
                                text-align: center;
                            `}
                        >
                            {siteMessage("RealTimePricing_PriceLoadFailed")}
                        </Typography>
                    )}
                </StyledWrapper>
            </CartLineContainer.Total>
            <CartLineContainer.Actions>
                <StyledWrapper {...styles.wishListAndCheckboxes}>
                    {(showAddToList ||
                        showCutWireButton ||
                        (showAddToCart && productInfo) ||
                        (showShipmentData && handleShowShipmentDataClick)) &&
                        !errorMessages.length &&
                        !cartLine.isRestricted && (
                            <StyledWrapper {...styles.ctaButtons}>
                                {showCutWireButton && (
                                    <Link {...styles.cutWireLink} onClick={openCutWireModal}>
                                        {cartLine.properties?.hasOwnProperty("cutWire") &&
                                        cartLine.properties["cutWire"] !== "" &&
                                        JSON.parse(cartLine.properties["cutWire"]).length > 0
                                            ? translate("Edit Cut Lengths")
                                            : translate("Add Cut Lengths")}
                                    </Link>
                                )}
                                {showAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                                    <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                        {translate("Shopping List")}
                                    </Link>
                                )}
                                {cartLine.isSubscription && cartLine.productSubscription && (
                                    <ProductDeliveryScheduleButton
                                        subscription={
                                            cartLine.properties.productSubscription
                                                ? JSON.parse(cartLine.properties.productSubscription)
                                                : cartLine.productSubscription
                                        }
                                        disabled={
                                            !cart.canModifyOrder ||
                                            cartLine.isPromotionItem ||
                                            !cartLine.qtyOrdered ||
                                            cartLine.qtyOrdered <= 0
                                        }
                                        onSave={saveScheduleHandler}
                                        extendedStyles={styles.deliveryScheduleButton}
                                    />
                                )}
                                {showAddToCart && productInfo && (
                                    <ProductContext.Provider
                                        value={
                                            {
                                                product,
                                                productInfo,
                                            } as any
                                        }
                                    >
                                        <StyledWrapper {...styles.addToCartButtonWrapper}>
                                            <ProductAddToCartButton
                                                extendedStyles={styles.addToCartButtonStyles}
                                                asLink={true}
                                                withIcon={true}
                                            />
                                        </StyledWrapper>
                                    </ProductContext.Provider>
                                )}
                                {showShipmentData && handleShowShipmentDataClick && (
                                    <Clickable
                                        {...styles.shipmentDataWrapper}
                                        onClick={() => {
                                            handleShowShipmentDataClick(cartLine.id);
                                        }}
                                    >
                                        <Icon {...styles.shipmentDataIcon} className="shipment-data-icon" />
                                        <Typography {...styles.shipmentDataText} className="shipment-data-text">
                                            {translate("Shipment Data")}
                                        </Typography>
                                    </Clickable>
                                )}
                            </StyledWrapper>
                        )}
                </StyledWrapper>
            </CartLineContainer.Actions>
            <CartLineContainer.Notes>
                {showLineNotes && !cartLine.isPromotionItem && cart?.properties["isPunchout"] === undefined && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable && !errorMessages.length}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                        showCutWire={!showCutWire}
                    />
                )}

                {showPONumber && cartLine.line && editable && (
                    <>
                        <StyledWrapper {...styles.poLineWrapper}>
                            <Link
                                onClick={() => setChangePONumerModalOpen(prev => !prev)}
                                {...styles.toggleLink}
                                icon={{ iconProps: { src: iconSrc } }}
                            >
                                {translate(heading)}
                            </Link>
                            {isChangePONumerModalOpen ? (
                                <TextField
                                    {...styles.editablePoTextField}
                                    id={`notes-${cartLine?.id}`}
                                    disabled={!cart?.canModifyOrder || isPoLineNumberUpdating}
                                    onChange={handleSetPoNumber}
                                    onBlur={onPoNumberBlurHandler}
                                    data-test-selector="cartline_notes"
                                    // XNG-Changes
                                    placeholder={translate("Add PO Line #")}
                                    label=""
                                    value={poLineNumber}
                                    maxLength={4000}
                                />
                            ) : (
                                <Typography {...styles.POLabel}>{poLineNumber}</Typography>
                            )}
                        </StyledWrapper>
                    </>
                )}
                {showPONumber && cartLine.line && !editable && (
                    <>
                        <SmallHeadingAndText
                            extendedStyles={styles.readOnlyPOLineText}
                            heading={translate("Product Note")}
                            text={poLineNumber}
                            data-test-selector="cartline_po_disabled"
                        />
                    </>
                )}
                {!errorMessages.length && !cartLine.isRestricted && (
                    // xng-change - using fieldset as the certifications checkboxes wrapper
                    <StyledWrapper as="fieldset" {...styles.certificationsStyles}>
                        {!dontShowCertification && !useModalForCertification && (
                            <>
                                <Checkbox
                                    checked={eisCertificate}
                                    data-test-selector={`line_eis_certificate_${cartLine.id}`}
                                    disabled={!editable}
                                    onChange={eisCertificationHandler}
                                    {...styles.certificationLabels}
                                >
                                    {siteMessage("Custom_site_request_certification")}
                                </Checkbox>
                                <Checkbox
                                    checked={mfrCertificate}
                                    disabled={!editable}
                                    data-test-selector={`line_mfr_certificate_${cartLine.id}`}
                                    onChange={mfrCertificationHandler}
                                    {...styles.certificationLabels}
                                >
                                    {translate("Request Mfr. Certification")}
                                </Checkbox>
                            </>
                        )}
                        {showPONumber && lineNumber && (
                            <StyledWrapper>
                                <Typography as="strong">{translate("PO Line #:")}</Typography>
                                <Typography
                                    css={css`
                                        margin-left: 5px;
                                    `}
                                >
                                    {lineNumber}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Notify
                                messageType="warning"
                                key={index}
                                data-test-selector="cartline_errorMessage"
                                sizeVariant="small"
                                showCloseButton={false}
                            >
                                {message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                )}
            </CartLineContainer.Notes>
            <CartLineContainer.Remove>
                {showRemoveAction && editable && !isCheckoutReviewAndSubmitPage && (
                    <Link
                        {...styles.removeLink}
                        onClick={removeCartLineClickHandler}
                        data-test-selector="cartline_removeLine"
                        disabled={removeClicked}
                    >
                        {translate("Remove")}
                    </Link>
                )}
            </CartLineContainer.Remove>
            {/* <Modal
                headline={translate("Change PO Line Number")}
                sizeVariant="small"
                isOpen={isChangePONumerModalOpen}
                handleClose={handleCloseChangePONumberModal}
            >
                <TextField label="PO Number" type="number" value={poLineNumber || 0} onChange={handleSetPoNumber} />
                <StyledWrapper className="modal-actions">
                    <Button variant="secondary" onClick={handleCloseChangePONumberModal}>
                        {translate("Close")}
                    </Button>
                    <Button variant="primary" onClick={handleChangePoLineNumber}>
                        {translate("Save")}
                    </Button>
                </StyledWrapper>
            </Modal> */}
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
